/**
 * InterviewFlow.studio — City Hall focus.
 * One wedge: answer permitting questions before applicants reach the counter.
 */
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InterviewFlowNavbar from '../../components/InterviewFlow/InterviewFlowNavbar';
import ErrorBoundary from '../../components/ErrorBoundary';

import { IF_BRAND, ensureInterviewFlowFonts } from '../../theme/interviewFlowBrand';

const DISPLAY = IF_BRAND.display;
const BODY = IF_BRAND.body;
const INK = IF_BRAND.ink;
const ACCENT = IF_BRAND.accent;
const STONE = IF_BRAND.stone;

const PORTALS = [
  {
    id: 'business',
    name: 'Business',
    tagline: 'What do I need to open or change a business?',
    href: '/v2/demo/permit/business',
    cta: 'Start business path',
  },
  {
    id: 'residential',
    name: 'Residential',
    tagline: 'ADUs, remodels, and home projects — which track?',
    href: '/v2/demo/permit/residential',
    cta: 'Start residential path',
  },
  {
    id: 'events',
    name: 'Events',
    tagline: 'Temporary use, street, and special-event guidance.',
    href: '/v2/demo/permit/events',
    cta: 'Start events path',
  },
  {
    id: 'zoning',
    name: 'Zoning check',
    tagline: 'Address → zone → use clearance before you apply.',
    href: '/zoning-check',
    cta: 'Check zoning',
  },
];

/** Live Public Sector stack — citizen + agency (City Hall on InterviewFlow). */
const PS_BASE = 'https://interviewflow.studio/publicSector';

const AUDIENCES = [
  {
    id: 'citizens',
    eyebrow: 'Citizens',
    name: 'Public applicant portal',
    tagline:
      'Apply online, track by code or email, pay fees, upload documents, and manage a citizen account — without agency credentials.',
    href: `${PS_BASE}/lnp/public`,
    cta: 'Open citizen portal',
    links: [
      { label: 'Apply', href: `${PS_BASE}/lnp/public/apply` },
      { label: 'Check status', href: `${PS_BASE}/lnp/public/status` },
      { label: 'Account', href: `${PS_BASE}/lnp/public/account` },
    ],
  },
  {
    id: 'agencies',
    eyebrow: 'Agencies',
    name: 'Staff permitting system',
    tagline:
      'Queues, record workspace, plan review, inspections, fees, GIS/parcels, CE/BNP/PNZ modules, and admin — for planners, reviewers, and clerks.',
    href: `${PS_BASE}/`,
    cta: 'Open agency portal',
    links: [
      { label: 'Staff sign-in', href: `${PS_BASE}/sign-in` },
      { label: 'LNP home', href: `${PS_BASE}/lnp/home` },
      { label: 'Plan review', href: `${PS_BASE}/lnp/plan-review` },
    ],
  },
];

const STEPS = [
  {
    title: 'Discover',
    body: 'InterviewFlow advisor — address and project intent → track, checklist, and CLAW trail before anyone visits City Hall.',
  },
  {
    title: 'Apply',
    body: 'Public Sector citizen portal — submit the application, track status, pay, and respond to deficiencies.',
  },
  {
    title: 'Process',
    body: 'Public Sector agency workspace — accept, review, inspect, fee, and issue on the live record.',
  },
];

function LandingContentInterviewFlow() {
  const navigate = useNavigate();

  useEffect(() => {
    ensureInterviewFlowFonts();
  }, []);

  return (
    <ErrorBoundary>
      <Box
        sx={{
          minHeight: '100vh',
          fontFamily: BODY,
          color: INK,
          bgcolor: '#dfe6ef',
        }}
      >
        <InterviewFlowNavbar />

        {/* Full-bleed civic plane */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            minHeight: { xs: '88vh', md: '100vh' },
            display: 'flex',
            alignItems: 'flex-end',
            overflow: 'hidden',
            background:
              'linear-gradient(180deg, rgba(12,26,46,0.15) 0%, rgba(12,26,46,0.55) 48%, rgba(12,26,46,0.88) 100%),'
              + 'linear-gradient(125deg, #6b8299 0%, #3d556e 38%, #1e3348 72%, #0c1a2e 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'repeating-linear-gradient(90deg, transparent 0, transparent 46px, rgba(255,255,255,0.04) 46px, rgba(255,255,255,0.04) 48px),'
                + 'repeating-linear-gradient(0deg, transparent 0, transparent 72px, rgba(255,255,255,0.03) 72px, rgba(255,255,255,0.03) 74px)',
              opacity: 0.85,
              animation: 'ifColumnDrift 28s linear infinite',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '8%',
              right: '8%',
              bottom: 0,
              height: { xs: '28%', md: '34%' },
              background:
                'linear-gradient(180deg, transparent, rgba(232,145,58,0.12) 40%, rgba(232,145,58,0.22))',
              clipPath: 'polygon(0 40%, 8% 0, 18% 35%, 28% 8%, 40% 42%, 52% 5%, 64% 38%, 76% 12%, 88% 40%, 100% 0, 100% 100%, 0 100%)',
              opacity: 0.9,
              animation: 'ifDomeGlow 6s ease-in-out infinite alternate',
              pointerEvents: 'none',
            },
            '@keyframes ifColumnDrift': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-48px)' },
            },
            '@keyframes ifDomeGlow': {
              '0%': { opacity: 0.55 },
              '100%': { opacity: 0.95 },
            },
          }}
          data-testid="if-cityhall-hero"
        >
          <Container
            maxWidth="md"
            sx={{
              position: 'relative',
              zIndex: 1,
              pb: { xs: 7, md: 10 },
              pt: { xs: 16, md: 18 },
              color: '#fff',
            }}
          >
            <Typography
              component="p"
              data-testid="if-brand"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: { xs: '2.75rem', md: '4.25rem' },
                letterSpacing: '-0.03em',
                lineHeight: 1.02,
                mb: 2,
                textShadow: '0 2px 24px rgba(0,0,0,0.25)',
                animation: 'ifBrandIn 0.9s ease-out both',
                '@keyframes ifBrandIn': {
                  from: { opacity: 0, transform: 'translateY(12px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              InterviewFlow
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 500,
                fontSize: { xs: '1.4rem', md: '1.85rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                mb: 1.75,
                maxWidth: 560,
                color: 'rgba(255,255,255,0.95)',
                animation: 'ifBrandIn 0.9s ease-out 0.12s both',
              }}
            >
              Answer applicants&apos; permitting questions before they get to City Hall
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.02rem', md: '1.12rem' },
                maxWidth: 520,
                mb: 3.5,
                color: 'rgba(255,255,255,0.72)',
                lineHeight: 1.55,
                animation: 'ifBrandIn 0.9s ease-out 0.22s both',
              }}
            >
              Discover the path with InterviewFlow — then apply and process in Public Sector, for citizens and agency staff.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ animation: 'ifBrandIn 0.9s ease-out 0.32s both' }}
            >
              <Button
                onClick={() => navigate('/v2/demo/permit')}
                variant="contained"
                size="large"
                data-testid="if-cta-permit-demo"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.35,
                  bgcolor: '#fff',
                  color: INK,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', boxShadow: 'none' },
                }}
              >
                Try the permit advisor
              </Button>
              <Button
                href="#city-system"
                variant="outlined"
                size="large"
                data-testid="if-cta-city-system"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 600,
                  px: 3,
                  py: 1.35,
                  borderColor: 'rgba(255,255,255,0.45)',
                  color: '#fff',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Citizens & agencies
              </Button>
              <Button
                href="/pitch-decks/yc.html"
                variant="text"
                size="large"
                data-testid="if-cta-pitch-deck"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 600,
                  px: 2,
                  py: 1.35,
                  color: 'rgba(255,255,255,0.85)',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  '&:hover': { bgcolor: 'transparent', color: '#fff' },
                }}
              >
                Pitch deck
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box
          id="portals"
          component="section"
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: '#f4f7fb',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              component="h2"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: { xs: '1.55rem', md: '1.9rem' },
                letterSpacing: '-0.02em',
                mb: 1,
              }}
            >
              Start where the citizen starts
            </Typography>
            <Typography sx={{ color: 'rgba(12, 26, 46, 0.65)', mb: 3.5, maxWidth: 520 }}>
              One advisor skill · portals for business, residential, events, and zoning.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: 2.5, md: 3 },
              }}
            >
              {PORTALS.map((p) => (
                <Box
                  key={p.id}
                  component="button"
                  type="button"
                  data-testid={`if-portal-${p.id}`}
                  onClick={() => navigate(p.href)}
                  sx={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: 'none',
                    borderBottom: `2px solid ${STONE}`,
                    borderRadius: 0,
                    bgcolor: 'transparent',
                    p: 0,
                    pb: 2,
                    fontFamily: BODY,
                    color: INK,
                    transition: 'border-color 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      borderColor: ACCENT,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Typography sx={{
                    fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.25rem', mb: 0.6, letterSpacing: '-0.02em',
                  }}
                  >
                    {p.name}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.95rem', color: 'rgba(12, 26, 46, 0.68)', lineHeight: 1.45, mb: 1.25, maxWidth: 360,
                  }}
                  >
                    {p.tagline}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: ACCENT }}>{p.cta} →</Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        <Box
          id="city-system"
          component="section"
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: '#e8eef5',
            borderTop: '1px solid rgba(12, 26, 46, 0.06)',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              component="h2"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: { xs: '1.55rem', md: '1.9rem' },
                letterSpacing: '-0.02em',
                mb: 1,
              }}
            >
              The city system — citizens and agencies
            </Typography>
            <Typography sx={{ color: 'rgba(12, 26, 46, 0.65)', mb: 3.5, maxWidth: 560 }}>
              Public Sector is the live permitting stack behind City Hall: a public applicant portal and a full staff workspace (LNP, plan review, inspections, fees, GIS, and more).
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: { xs: 4, md: 5 },
              }}
            >
              {AUDIENCES.map((a) => (
                <Box
                  key={a.id}
                  data-testid={`if-audience-${a.id}`}
                  sx={{
                    borderTop: `3px solid ${a.id === 'citizens' ? ACCENT : '#0c1a2e'}`,
                    pt: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: a.id === 'citizens' ? ACCENT : INK,
                      mb: 0.75,
                    }}
                  >
                    {a.eyebrow}
                  </Typography>
                  <Typography sx={{
                    fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.3rem', mb: 0.75, letterSpacing: '-0.02em',
                  }}
                  >
                    {a.name}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.95rem', color: 'rgba(12, 26, 46, 0.68)', lineHeight: 1.5, mb: 2, maxWidth: 420,
                  }}
                  >
                    {a.tagline}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {a.links.map((l) => (
                      <Button
                        key={l.href}
                        component="a"
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: INK,
                          borderBottom: `1px solid ${STONE}`,
                          borderRadius: 0,
                          px: 0,
                          minWidth: 0,
                          '&:hover': { color: ACCENT, bgcolor: 'transparent', borderColor: ACCENT },
                        }}
                      >
                        {l.label}
                      </Button>
                    ))}
                  </Stack>
                  <Button
                    component="a"
                    href={a.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    data-testid={`if-cta-${a.id}`}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: a.id === 'citizens' ? ACCENT : INK,
                      boxShadow: 'none',
                      px: 2.5,
                      '&:hover': {
                        bgcolor: a.id === 'citizens' ? '#1565c0' : '#1a2f4a',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {a.cta} →
                  </Button>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff' }}>
          <Container maxWidth="md">
            <Typography
              component="h2"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: { xs: '1.55rem', md: '1.9rem' },
                letterSpacing: '-0.02em',
                mb: 1,
                textAlign: 'center',
              }}
            >
              From the living room to the counter
            </Typography>
            <Typography sx={{
              color: 'rgba(12, 26, 46, 0.65)', mb: 4, textAlign: 'center', maxWidth: 520, mx: 'auto',
            }}
            >
              Advisor discovers the path · citizen portal submits · agency system processes the record.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 3.5,
              }}
            >
              {STEPS.map((step, i) => (
                <Box key={step.title}>
                  <Typography
                    sx={{
                      fontFamily: DISPLAY,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: ACCENT,
                      mb: 0.75,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {i + 1}. {step.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(12, 26, 46, 0.72)', lineHeight: 1.5 }}>{step.body}</Typography>
                </Box>
              ))}
            </Box>
            <Stack direction="row" justifyContent="center" sx={{ mt: 4.5 }}>
              <Button
                onClick={() => navigate('/v2/demo/permit')}
                variant="contained"
                size="large"
                data-testid="if-cta-claw-demo"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 700,
                  textTransform: 'none',
                  bgcolor: ACCENT,
                  px: 3.5,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1565c0', boxShadow: 'none' },
                }}
              >
                See how CLAW decided
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box id="build" component="section" sx={{ py: { xs: 5, md: 7 }, bgcolor: '#eef2f7' }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: { xs: '1.4rem', md: '1.7rem' },
                letterSpacing: '-0.02em',
                mb: 1.5,
              }}
            >
              For city teams that build
            </Typography>
            <Typography sx={{
              color: 'rgba(12, 26, 46, 0.68)', mb: 3, maxWidth: 480, mx: 'auto',
            }}
            >
              Configure jurisdiction catalogs, fees, and the same CLAW skill in Studio — public preview today, live loop when signed in.
            </Typography>
            <Button
              onClick={() => navigate('/studio')}
              variant="outlined"
              sx={{
                fontFamily: BODY,
                fontWeight: 700,
                textTransform: 'none',
                borderColor: ACCENT,
                color: ACCENT,
                px: 3,
              }}
            >
              Open Studio
            </Button>
          </Container>
        </Box>

        <Box
          component="footer"
          sx={{
            py: 4,
            borderTop: '1px solid rgba(12, 26, 46, 0.08)',
            textAlign: 'center',
            bgcolor: '#f4f7fb',
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', color: 'rgba(12, 26, 46, 0.55)', mb: 1 }}>
            © {new Date().getFullYear()} InterviewFlow · Public Sector.
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(12, 26, 46, 0.42)', mb: 1.25 }}>
            Discover · apply · process — all on interviewflow.studio
          </Typography>
          <Button
            href="/pitch-decks/yc.html"
            data-testid="if-footer-pitch-deck"
            sx={{
              fontFamily: BODY,
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'none',
              color: ACCENT,
              minWidth: 0,
              px: 1,
            }}
          >
            YC · City Hall pitch deck
          </Button>
        </Box>
      </Box>
    </ErrorBoundary>
  );
}

export default LandingContentInterviewFlow;
